import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaTimes } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import ArticleComponent from './ArticleComponent';

function SearchModal({
    isOpen,
    onClose,
    onSearch,
    initialQuery = '',
    isSearching: externalIsSearching = false,
    searchResults: externalResults = null,
    searchError: externalError = null
}) {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [searchQuery, setSearchQuery] = useState(initialQuery);
    const [hasSearched, setHasSearched] = useState(false);
    const searchInputRef = useRef(null);
    const searchResultsRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            setSearchQuery(initialQuery);
            setHasSearched(false);
            setTimeout(() => {
                if (searchInputRef.current) {
                    searchInputRef.current.focus();
                }
            }, 100);
        }
    }, [isOpen, initialQuery]);

    const handleClose = () => {
        setSearchQuery('');
        setHasSearched(false);
        onClose();
    };

    const handleSearchSubmit = async (e) => {
        e.preventDefault();

        if (!searchQuery.trim()) {
            alert('Veuillez saisir un terme de recherche');
            return;
        }

        setHasSearched(true);

        if (onSearch) {
            await onSearch(searchQuery);
        }
    };

    const handleArticleClick = (article) => {
        // Utiliser à la fois l'ID et le SLUG comme attendu par la route
        navigate(`/article/${article.id}/${article.slug}`);
        handleClose();
    };

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            handleClose();
        }
    };

    // Transformer les articles du format API vers le format attendu par ArticleComponent (comme dans Articles.jsx)
    const transformArticlesForComponent = (articles) => {
    if (!articles || !Array.isArray(articles)) return [];
    
    return articles.map(article => ({
        id: article.id, // ✅ IMPORTANT : Conserver l'ID
        slug: article.slug,
        link: article.link || `/article/${article.id}/${article.slug}`, // ✅ Utiliser id+slug
        date: article.date?.date 
            ? `${article.articleCategory || ''} | ${new Date(article.date.date).toLocaleDateString('fr-FR', {
                day: "2-digit",
                month: "long",
                year: "numeric"
            })}`
            : article.articleCategory || '',
        title: article.title,
        image: article.banner
            ? article.banner.replace(/([^:]\/)\/+/g, "$1")
            : "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcR8bikI-KUuM1IWosgqDRS5jyv2U_PPYlG6Tg&s",
        alt: article.slug,
        category: article.articleCategory,
        fullArticle: article
    }));
};

    const getTotalResults = () => {
        if (!externalResults) return 0;
        let total = 0;
        if (externalResults.articles) total += externalResults.articles.length;
        if (externalResults.members) total += externalResults.members.length;
        if (externalResults.services) total += externalResults.services.length;
        if (externalResults.consules) total += externalResults.consules.length;
        if (externalResults.partners) total += externalResults.partners.length;
        if (externalResults.entership) total += externalResults.entership.length;
        return total;
    };

    const showLoading = hasSearched && externalIsSearching;
    const showResults = hasSearched && !externalIsSearching && externalResults !== null;
    const showError = hasSearched && !externalIsSearching && externalError !== null;

    const transformedArticles = externalResults?.articles ? transformArticlesForComponent(externalResults.articles) : [];

    if (!isOpen) return null;

    return (
        <>
            <style type="text/css">
                {`
                    .search-modal-backdrop {
                        position: fixed;
                        top: 0;
                        left: 0;
                        width: 100%;
                        height: 100%;
                        background-color: #0a2b57b9;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        z-index: 2000;
                        padding: 20px;
                    }

                    .search-modal-content {
                        background: #e7ebf0;
                        width: 90%;
                        max-width: 1300px;
                        height: 85vh;
                        border-radius: 0px;
                        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
                        overflow: hidden;
                        display: flex;
                        flex-direction: column;
                    }

                    .search-modal-header {
                        background: none;
                        color: white;
                        padding: 1.5rem;
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        flex-shrink: 0;
                        border-bottom: 1px solid #ddd;
                    }

                    .search-modal-title {
                        margin: 0;
                        font-size: clamp(1.5rem, 4vw, 2.2rem);
                        font-weight: 700;
                        color: #1e1e1eff
                    }

                    .search-modal-close {
                        background: none;
                        border: 1px solid #1e1e1eff;
                        color: #1e1e1eff;
                        font-size: 1.5rem;
                        cursor: pointer;
                        padding: 10px;
                        width: 80px;
                        height: 65px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        border-radius: 10%;
                        transition: background-color 0.3s ease;
                    }

                    .search-modal-close:hover {
                        background-color: #ffffff33;
                    }

                    .search-modal-body {
                        padding: clamp(2rem, 5vw, 2rem);
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        flex-grow: 1;
                        padding-top: 0px;
                        overflow: hidden;
                    }

                    .search-form {
                        display: flex;
                        flex-direction: column;
                        gap: 0;
                        width: 100%;
                        max-width: 800px;
                    }

                    .search-input-group {
                        display: flex;
                        gap: 0;
                        width: 100%;
                    }

                    .search-input {
                        flex: 1;
                        padding: 1rem;
                        border: 2px solid #e0e0e0;
                        border-right: none;
                        border-radius: 4px 0 0 4px;
                        font-size: clamp(1rem, 2.5vw, 1.3rem);
                        font-weight: 600;
                        outline: none;
                        min-height: 65px;
                    }

                    .search-input:focus {
                        outline: none !important;
                        box-shadow: none !important;
                        border-color: #0a2c57 !important;
                    }

                    .search-submit-btn {
                        background-color: #ffbe00;
                        border: none;
                        padding: 0 1.5rem;
                        border-radius: 0 4px 4px 0;
                        max-height: 65px;
                        min-height: 65px;
                        cursor: pointer;
                        transition: background-color 0.3s ease;
                        white-space: nowrap;
                        color: #0a2c57;
                        font-size: clamp(16px, 3vw, 20px);
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    }

                    .search-submit-btn:hover:not(:disabled) {
                        background-color: #e5ab00;
                    }

                    .search-submit-btn:disabled {
                        background-color: #cccccc;
                        cursor: not-allowed;
                    }

                    .search-results-container {
                        width: 100%;
                        height: 100%;
                        
                        padding: 10px;
                    }

                    .search-results-scrollable {
                        width: 100%;
                        height: calc(100% - 60px);
                        overflow-y: auto;
                        padding-right: 10px;
                    }

                    .search-results-scrollable::-webkit-scrollbar {
                        width: 8px;
                    }

                    .search-results-scrollable::-webkit-scrollbar-track {
                        background: #f1f1f1;
                        border-radius: 4px;
                    }

                    .search-results-scrollable::-webkit-scrollbar-thumb {
                        background: #0a2c57;
                        border-radius: 4px;
                    }

                    .search-results-scrollable::-webkit-scrollbar-thumb:hover {
                        background: #1880e7;
                    }

                    .search-result-section {
                        margin-bottom: 2.5rem;
                        // background: white;
                        border-radius: 8px;
                        
                        box-shadow: 0 2px 8px rgba(0,0,0,0.05);
                    }

                    .search-section-title {
                        color: #1a3c5e;
                        margin: 0 0 1.5rem 0;
                        padding-bottom: 0.75rem;
                        // border-bottom: 3px solid #132a72;
                        font-size: 1.3rem;
                        font-weight: 700;
                        text-align: left;
                    }

                    .search-result-count {
                        display: inline-block;
                        background: #0a2c57;
                        color: white;
                        padding: 0.25rem 0.75rem;
                        border-radius: 20px;
                        font-size: 0.8rem;
                        margin-left: 0.5rem;
                        vertical-align: middle;
                    }

                    .search-result-grid {
                        display: grid;
                        grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
                        gap: 1.5rem;
                    }

                    .search-result-card {
                        border: 1px solid #e0e0e0;
                        border-radius: 8px;
                        overflow: hidden;
                        transition: all 0.3s ease;
                        background: white;
                        height: 100%;
                        display: flex;
                        flex-direction: column;
                    }

                    .search-result-card:hover {
                        transform: translateY(-4px);
                        box-shadow: 0 6px 15px rgba(0,0,0,0.1);
                        border-color: #0a2c57;
                    }

                    .search-result-image {
                        width: 100%;
                        height: 180px;
                        object-fit: cover;
                        background: #f5f5f5;
                    }

                    .search-result-content {
                        padding: 1.25rem;
                        flex-grow: 1;
                        display: flex;
                        flex-direction: column;
                    }

                    .search-result-title {
                        margin: 0 0 0.75rem 0;
                        font-size: 1rem;
                        color: #0a2c57;
                        font-weight: 700;
                        line-height: 1.4;
                    }

                    .search-result-description {
                        font-size: 0.85rem;
                        color: #666;
                        margin: 0 0 1rem 0;
                        line-height: 1.5;
                        flex-grow: 1;
                        display: -webkit-box;
                        -webkit-line-clamp: 3;
                        -webkit-box-orient: vertical;
                        overflow: hidden;
                    }

                    .search-result-meta {
                        display: flex;
                        justify-content: space-between;
                        font-size: 0.75rem;
                        color: #888;
                        margin-top: auto;
                    }

                    .search-result-meta-item {
                        display: flex;
                        align-items: center;
                        gap: 0.25rem;
                    }

                    .search-list-item {
                        padding: 1rem;
                        border: 1px solid #e0e0e0;
                        border-radius: 8px;
                        margin-bottom: 0.75rem;
                        background: white;
                        transition: all 0.2s ease;
                    }

                    .search-list-item:hover {
                        border-color: #0a2c57;
                        background: #f8f9fa;
                    }

                    .search-list-title {
                        margin: 0 0 0.5rem 0;
                        font-size: 0.95rem;
                        color: #0a2c57;
                        font-weight: 600;
                    }

                    .search-list-details {
                        font-size: 0.8rem;
                        color: #666;
                        margin: 0;
                    }

                    .search-list-details strong {
                        color: #333;
                    }

                    .no-results-section {
                        text-align: center;
                        padding: 3rem;
                        background: white;
                        border-radius: 8px;
                        margin: 1rem 0;
                    }

                    .no-results-icon {
                        font-size: 3rem;
                        color: #ccc;
                        margin-bottom: 1rem;
                    }

                    .no-results-title {
                        color: #666;
                        margin-bottom: 0.5rem;
                        font-size: 1.2rem;
                    }

                    .no-results-message {
                        color: #888;
                        margin-bottom: 1.5rem;
                    }

                    .new-search-btn {
                        padding: 0.75rem 1.5rem;
                        background: #0a2c57;
                        color: white;
                        border: none;
                        border-radius: 4px;
                        cursor: pointer;
                        font-weight: 600;
                        transition: all 0.3s ease;
                        font-size: 0.9rem;
                    }

                    .new-search-btn:hover {
                        background: #1880e7;
                        transform: translateY(-2px);
                    }

                    .back-to-search-btn {
                        padding: 0.5rem 1rem;
                        background: transparent;
                        color: #0a2c57;
                        border: 1px solid #0a2c57;
                        border-radius: 4px;
                        cursor: pointer;
                        font-weight: 600;
                        transition: all 0.3s ease;
                    }

                    .back-to-search-btn:hover {
                        background: #0a2c57;
                        color: white;
                    }

                    .search-results-header {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        margin-bottom: 1.5rem;
                        padding-bottom: 1rem;
                        border-bottom: 1px solid #e0e0e0;
                    }

                    .search-results-summary {
                        color: #0a2c57;
                        font-size: 1.1rem;
                        font-weight: 600;
                    }

                    .search-keyword {
                        color: #ffbe00;
                        font-weight: 700;
                    }

                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }

                    .loading-spinner {
                        width: 40px;
                        height: 40px;
                        border: 3px solid #f3f3f3;
                        border-top: 3px solid #1a3c5e;
                        border-radius: 50%;
                        animation: spin 1s linear infinite;
                        margin: 0 auto 1rem;
                    }

                    .loading-text {
                        text-align: center;
                        color: #666;
                        font-size: 1rem;
                    }

                    .error-container {
                        padding: 2rem;
                        background: #ffe6e6;
                        border-radius: 8px;
                        text-align: center;
                        border: 1px solid #ffcccc;
                    }

                    .error-icon {
                        font-size: 2.5rem;
                        color: #d32f2f;
                        margin-bottom: 1rem;
                    }

                    .error-title {
                        color: #d32f2f;
                        margin-bottom: 0.5rem;
                        font-size: 1.2rem;
                    }

                    .error-message {
                        color: #666;
                        margin-bottom: 1.5rem;
                    }

                    .article-category {
                        background: #e3f2fd;
                        color: #0a2c57;
                        padding: 0.25rem 0.75rem;
                        border-radius: 4px;
                        font-size: 0.7rem;
                        font-weight: 600;
                    }

                    .service-price {
                        color: #2e7d32;
                        font-weight: 700;
                        font-size: 0.9rem;
                    }

                    .consul-country {
                        display: inline-block;
                        background: #f3e5f5;
                        color: #7b1fa2;
                        padding: 0.2rem 0.6rem;
                        border-radius: 4px;
                        font-size: 0.7rem;
                        margin-left: 0.5rem;
                    }

                    .partner-category {
                        color: #666;
                        font-size: 0.75rem;
                        font-style: italic;
                    }
                `}
            </style>

            <div className="search-modal-backdrop" onClick={handleBackdropClick}>
                <div className="search-modal-content">
                    <div className="search-modal-header">
                        <h2 className="search-modal-title">
                            {showResults ? `Résultats pour "${searchQuery}"` : t('search.title')}
                        </h2>
                        <button
                            className="search-modal-close"
                            onClick={handleClose}
                            aria-label="Fermer"
                        >
                            <FaTimes />
                        </button>
                    </div>
                    <div className="search-modal-body">
                        {!showResults ? (
                            <form className="search-form" onSubmit={handleSearchSubmit}>
                                <div className="search-input-group">
                                    <input
                                        ref={searchInputRef}
                                        type="text"
                                        className="search-input"
                                        placeholder={t('search.placeholder')}
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        autoComplete="off"
                                    />
                                    <button
                                        type="submit"
                                        className="search-submit-btn"
                                        disabled={!searchQuery.trim() || showLoading}
                                    >
                                        {showLoading ? (
                                            <div className="loading-spinner" style={{
                                                width: '20px',
                                                height: '20px',
                                                border: '2px solid #fff',
                                                borderTop: '2px solid #0a2c57'
                                            }}></div>
                                        ) : (
                                            <i className="fi fi-ss-paper-plane-top"></i>
                                        )}
                                    </button>
                                </div>
                            </form>
                        ) : (
                            <div className="search-results-container">
                                <div className="search-results-header">
                                    <div className="search-results-summary">
                                        {getTotalResults()} résultat(s) pour "<span className="search-keyword">{searchQuery}</span>"
                                    </div>
                                    <button
                                        onClick={() => {
                                            setHasSearched(false);
                                        }}
                                        className="back-to-search-btn"
                                    >
                                        Nouvelle recherche
                                    </button>
                                </div>

                                <div className="search-results-scrollable" ref={searchResultsRef}>
                                    {showError ? (
                                        <div className="error-container">
                                            <div className="error-icon">⚠️</div>
                                            <h3 className="error-title">Erreur</h3>
                                            <p className="error-message">{externalError}</p>
                                            <button
                                                onClick={() => setHasSearched(false)}
                                                className="new-search-btn"
                                            >
                                                Nouvelle recherche
                                            </button>
                                        </div>
                                    ) : (
                                        <>
                                            {/* ARTICLES - Utilisation du même composant que dans Articles.jsx */}
                                            {transformedArticles.length > 0 && (
                                                <div className="search-result-section">
                                                    <h3 className="search-section-title">
                                                        Articles
                                                        <span className="search-result-count">{transformedArticles.length}</span>
                                                    </h3>
                                                    <ArticleComponent
                                                        articles={transformedArticles}
                                                        onArticleClick={handleArticleClick}
                                                    />
                                                </div>
                                            )}

                                            {/* MEMBRES */}
                                            {externalResults?.members && externalResults.members.length > 0 && (
                                                <div className="search-result-section">
                                                    <h3 className="search-section-title">
                                                        Membres
                                                        <span className="search-result-count">{externalResults.members.length}</span>
                                                    </h3>
                                                    <div>
                                                        {externalResults.members.map((member, index) => (
                                                            <div key={index} className="search-list-item">
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                                                    {member.picture && (
                                                                        <img
                                                                            src={member.picture}
                                                                            alt={member.name}
                                                                            style={{
                                                                                width: '60px',
                                                                                height: '60px',
                                                                                borderRadius: '50%',
                                                                                objectFit: 'cover'
                                                                            }}
                                                                            onError={(e) => {
                                                                                e.target.src = 'https://via.placeholder.com/60/0a2c57/ffffff?text=' + member.name.charAt(0);
                                                                            }}
                                                                        />
                                                                    )}
                                                                    <div style={{ flex: 1 }}>
                                                                        <h4 className="search-list-title">{member.name}</h4>
                                                                        <p className="search-list-details">
                                                                            <strong>Poste:</strong> {member.poste}
                                                                            <br />
                                                                            <strong>Catégorie:</strong> {member.category}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* SERVICES */}
                                            {externalResults?.services && externalResults.services.length > 0 && (
                                                <div className="search-result-section">
                                                    <h3 className="search-section-title">
                                                        Services
                                                        <span className="search-result-count">{externalResults.services.length}</span>
                                                    </h3>
                                                    <div>
                                                        {externalResults.services.map((service, index) => (
                                                            <div key={index} className="search-list-item">
                                                                <h4 className="search-list-title">{service.title}</h4>
                                                                <p className="search-list-details">
                                                                    <strong>Prix:</strong> <span className="service-price">{service.price}</span>
                                                                    <br />
                                                                    <strong>Durée:</strong> {service.duree}
                                                                    <br />
                                                                    <strong>Catégorie:</strong> {service.categoryService}
                                                                </p>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* CONSULS HONORAIRES */}
                                            {externalResults?.consules && externalResults.consules.length > 0 && (
                                                <div className="search-result-section">
                                                    <h3 className="search-section-title">
                                                        Consuls honoraires
                                                        <span className="search-result-count">{externalResults.consules.length}</span>
                                                    </h3>
                                                    <div className="search-result-grid">
                                                        {externalResults.consules.map((consul, index) => (
                                                            <div key={index} className="search-result-card">
                                                                <div className="search-result-content">
                                                                    <h4 className="search-result-title">
                                                                        {consul.civility} {consul.name}
                                                                        <span className="consul-country">{consul.country}</span>
                                                                    </h4>
                                                                    <p className="search-list-details">
                                                                        <strong>Ville:</strong> {consul.townRepresent}
                                                                        <br />
                                                                        <strong>Adresse:</strong> {consul.adress}
                                                                        <br />
                                                                        <strong>Téléphone:</strong> {consul.phone}
                                                                        <br />
                                                                        <strong>Email:</strong> {consul.email}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* PARTENAIRES */}
                                            {externalResults?.partners && externalResults.partners.length > 0 && (
                                                <div className="search-result-section">
                                                    <h3 className="search-section-title">
                                                        Partenaires
                                                        <span className="search-result-count">{externalResults.partners.length}</span>
                                                    </h3>
                                                    <div className="search-result-grid">
                                                        {externalResults.partners.map((partner, index) => (
                                                            <div key={index} className="search-result-card">
                                                                <div className="search-result-content">
                                                                    <h4 className="search-result-title">{partner.name}</h4>
                                                                    <p className="search-list-details">
                                                                        <strong>Lieu:</strong> {partner.town}, {partner.details}
                                                                        <br />
                                                                        <span className="partner-category">{partner.categoryPartner}</span>
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* ENTREPRENEURIAT */}
                                            {externalResults?.entership && externalResults.entership.length > 0 && (
                                                <div className="search-result-section">
                                                    <h3 className="search-section-title">
                                                        Entrepreneuriat
                                                        <span className="search-result-count">{externalResults.entership.length}</span>
                                                    </h3>
                                                    <div className="search-result-grid">
                                                        {externalResults.entership.map((item, index) => (
                                                            <div key={index} className="search-result-card">
                                                                {item.picture && (
                                                                    <img
                                                                        src={item.picture}
                                                                        alt={item.name}
                                                                        className="search-result-image"
                                                                        onError={(e) => {
                                                                            e.target.src = 'https://via.placeholder.com/300x150/0a2c57/ffffff?text=' + item.name;
                                                                        }}
                                                                    />
                                                                )}
                                                                <div className="search-result-content">
                                                                    <h4 className="search-result-title">{item.name}</h4>
                                                                    <p className="search-result-description">
                                                                        {item.content?.replace(/<[^>]*>/g, '').substring(0, 150)}...
                                                                    </p>
                                                                    <div className="search-result-meta">
                                                                        <div className="search-result-meta-item">
                                                                            🔗 <a href={item.linkRedirection} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>{item.textButton}</a>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* AUCUN RÉSULTAT */}
                                            {getTotalResults() === 0 && !showLoading && !showError && (
                                                <div className="no-results-section">
                                                    <div className="no-results-icon">🔍</div>
                                                    <h3 className="no-results-title">Aucun résultat trouvé</h3>
                                                    <p className="no-results-message">
                                                        Aucun résultat n'a été trouvé pour "<strong>{searchQuery}</strong>".
                                                        Essayez avec d'autres mots-clés.
                                                    </p>
                                                    <button
                                                        onClick={() => setHasSearched(false)}
                                                        className="new-search-btn"
                                                    >
                                                        Nouvelle recherche
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

export default SearchModal;